import { Alert } from '@quality24/inpatient-typings';
import {
  ComposedPatientCallReason,
  PatientCallReasonQueryResult,
  QueryResult,
  SubscriptionResult,
} from './types';

/**
 * Recursively removes alerts from inner children
 * @param children
 * @param alert
 * @returns
 */
export const updateInnerChildren = (
  children: PatientCallReasonQueryResult['children'],
  alert: Pick<
    Alert,
    'id' | 'reasonId' | 'groupId' | 'resolvedAt' | 'cancelledAt'
  >,
): PatientCallReasonQueryResult['children']['nodes'] => {
  // remove active alert from child
  if (children.nodes?.length) {
    const result = children.nodes.map((child) => {
      if (child.id === alert.groupId) {
        return {
          ...child,
          activeAlerts: { ...child.service.activeAlerts, nodes: [] },
        };
      }

      // Has inner children
      if (child.children?.totalCount && child.children.totalCount > 0) {
        const children2 = updateInnerChildren(
          child.children as PatientCallReasonQueryResult['children'],
          alert,
        );
        return { ...child, children: { ...child.children, nodes: children2 } };
      }
      return child;
    });

    return result;
  }

  return children.nodes;
};

/**
 * Recursively inserts alerts to inner children
 * @param children
 * @param alert
 * @returns
 */
export const insertInnerChildren = (
  children: PatientCallReasonQueryResult['children'],
  alert: Required<Pick<Alert, 'id' | 'reasonId' | 'groupId' | 'createdAt'>> &
    Pick<Alert, 'resolvedAt' | 'cancelledAt'>,
): PatientCallReasonQueryResult['children']['nodes'] => {
  // remove active alert from child
  if (children.nodes?.length) {
    const result = children.nodes.map((child) => {
      if (child.id === alert.groupId) {
        return {
          ...child,
          lastAlert: { ...child.service.lastAlert, nodes: [alert] },
          activeAlerts: { ...child.service.activeAlerts, nodes: [alert] },
        };
      }

      // Has inner children
      if (child.children?.totalCount && child.children.totalCount > 0) {
        const children2 = insertInnerChildren(
          child.children as PatientCallReasonQueryResult['children'],
          alert,
        );
        return { ...child, children: { ...child.children, nodes: children2 } };
      }
      return child;
    });

    return result;
  }

  return children.nodes;
};

/**
 * Handles a new alert changes event
 * @param {*} prev
 * @param {*} param1
 */
export const handleAlertChanges = (
  prev: QueryResult,
  { subscriptionData }: SubscriptionResult,
) => {
  if (!subscriptionData.data) return prev;
  const { event, alertData: alert } = subscriptionData.data.alertChanges;

  let reasons;
  switch (event) {
    case 'insert-alertEvent':
    case 'update': {
      if (!alert.resolvedAt && !alert.cancelledAt) return prev;
      reasons = prev.groups.nodes.map((n) => {
        // remove active alert from child
        if (n.children.nodes?.length) {
          const children = updateInnerChildren(n.children, alert);
          return { ...n, children: { ...n.children, nodes: children } };
        }
        // remove active alert from parent
        if (n.id === alert.reasonId) {
          return {
            ...n,
            activeAlerts: { ...n.service.activeAlerts, nodes: [] },
          };
        }
        return n;
      });
      break;
    }

    case 'insert':
      reasons = prev.groups.nodes.map((n) => {
        if (n.children.nodes?.length) {
          const children = insertInnerChildren(n.children, alert);
          return { ...n, children: { ...n.children, nodes: children } };
        }
        // add active alert
        if (n.id === alert.reasonId) {
          return {
            ...n,
            lastAlert: { ...n.service.lastAlert, nodes: [alert] },
            activeAlerts: { ...n.service.activeAlerts, nodes: [alert] },
          };
        }
        return n;
      });
      break;

    default:
      return prev;
  }

  return { ...prev, groups: { ...prev.groups, nodes: reasons } };
};

/**
 * Converts PatientCallReasonQueryResult types to PatientCallGroup
 * @param reasons
 * @returns
 */
export const parseGroups = (
  groups?: Array<PatientCallReasonQueryResult>,
): Array<ComposedPatientCallReason> => {
  if (!groups) return [];
  return groups.map((group) => ({
    ...group.service,
    reason: group.name || group.service.reason,
    // Display label: server-translated custom group name override falling
    // back to the server-translated service reason.
    reasonI18N: group.nameI18N || group.service.reasonI18N,
    groupId: group.id,
    modifier: group.modifier || group.service.modifier,
    conditional: group.conditional || group.service.conditional,
    lastAlert: group.service.lastAlert?.nodes[0] || null,
    activeAlert: group.service.activeAlerts?.nodes
      ? group.service.activeAlerts.nodes[0]
      : undefined,
    hasChildren: !!group.children.totalCount && group.children.totalCount > 0,
    chargeableItems: group.service.chargeableItems?.nodes || [],
    paymentConfig: group.service.paymentConfig,
    serviceCustomFields: group.service.serviceCustomFields?.nodes || [],
    children: group.children.nodes
      ? group.children.nodes.map((child) => ({
          ...child.service,
          reason: child.name || child.service.reason,
          reasonI18N: child.nameI18N || child.service.reasonI18N,
          groupId: child.id,
          modifier: child.modifier || child.service.modifier,
          conditional: child.conditional || child.service.conditional,
          lastAlert: child.service.lastAlert?.nodes[0] || null,
          activeAlert: child.service.activeAlerts?.nodes
            ? child.service.activeAlerts.nodes[0]
            : undefined,
          chargeableItems: child.service.chargeableItems?.nodes || [],
          paymentConfig: child.service.paymentConfig,
          serviceCustomFields: child.service.serviceCustomFields?.nodes || [],
          // Second level children
          // TODO: Should be replaced with a logic to lazy loading these children
          hasChildren:
            !!child.children.totalCount && child.children.totalCount > 0,
          children: child.children.nodes
            ? child.children.nodes.map((child2) => ({
                ...child2.service,
                reason: child2.name || child2.service.reason,
                reasonI18N: child2.nameI18N || child2.service.reasonI18N,
                groupId: child2.id,
                modifier: child2.modifier || child2.service.modifier,
                conditional: child2.conditional || child2.service.conditional,
                lastAlert: child2.service.lastAlert?.nodes[0] || null,
                activeAlert: child2.service.activeAlerts?.nodes
                  ? child2.service.activeAlerts.nodes[0]
                  : undefined,
                chargeableItems: child2.service.chargeableItems?.nodes || [],
                paymentConfig: child2.service.paymentConfig,
                serviceCustomFields:
                  child2.service.serviceCustomFields?.nodes || [],
              }))
            : undefined,
        }))
      : undefined,
  }));
};
